import React, { useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, Link2Off, Search, Users } from 'lucide-react';
import { MATCH_TYPE_LABELS } from '../lib/loadStatus';

const PILL = {
  matched: { cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2, label: 'Matched' },
  needs_confirmation: { cls: 'bg-amber-100 text-amber-800', Icon: AlertTriangle, label: 'Needs confirmation' },
  unlinked: { cls: 'bg-slate-200 text-slate-700', Icon: Link2Off, label: 'Unlinked' },
};

export const normalizeMatchStatus = (s) => {
  if (s === 'matched') return 'matched';
  if (s === 'ambiguous' || s === 'needs_confirmation') return 'needs_confirmation';
  return 'unlinked';
};

export const DriverMatchPill = ({ status, matchType, testId }) => {
  const p = PILL[normalizeMatchStatus(status)];
  const Icon = p.Icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${p.cls}`}>
      <Icon className="w-3 h-3" /> {p.label}{matchType && status === 'matched' ? ` · ${MATCH_TYPE_LABELS[matchType] || matchType}` : ''}
    </span>
  );
};

/** Compact searchable driver list (active drivers only). Calls onPick(driver). */
export const DriverSearchList = ({ drivers, onPick, testIdPrefix = 'driver-search', excludeIds = [], autoFocus = false }) => {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (drivers || [])
      .filter((d) => !excludeIds.includes(d.user_id || d.driver_id || d.id))
      .filter((d) => !needle || (d.name || '').toLowerCase().includes(needle) || (d.email || '').toLowerCase().includes(needle))
      .slice(0, 8);
  }, [drivers, q, excludeIds]);
  return (
    <div className="mt-1.5">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
        <input
          autoFocus={autoFocus}
          data-testid={`${testIdPrefix}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search registered drivers…"
          className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded-md text-xs"
        />
      </div>
      <ul className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100 bg-white max-h-44 overflow-y-auto" data-testid={`${testIdPrefix}-results`}>
        {list.length === 0 && <li className="px-2 py-2 text-[11px] text-slate-400 italic">No registered drivers match</li>}
        {list.map((d) => {
          const id = d.user_id || d.driver_id || d.id;
          return (
            <li key={id}>
              <button type="button" data-testid={`${testIdPrefix}-pick-${id}`} onClick={() => onPick({ ...d, driver_id: id })}
                className="w-full text-left px-2 py-1.5 hover:bg-[#002FA7]/5 text-xs">
                <span className="font-semibold text-slate-800">{d.name}</span>
                {d.email && <span className="text-slate-500"> · {d.email}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * One review-table cell shared by the Amazon Relay and Excel/CSV importers.
 * Shows: original imported name → registered driver, match pill, candidate confirmation, manual search.
 * onChange(driverId|null, matchType|null, driverName|null)
 */
export const DriverMatchCell = ({
  originalName, matchStatus, matchType, driverId, driverName, candidates = [], drivers = [],
  onChange, sameNameCount = 0, onApplyToAll, testIdPrefix,
}) => {
  const [picking, setPicking] = useState(false);
  const status = driverId ? 'matched' : normalizeMatchStatus(matchStatus);
  const cands = (candidates || []).map((c) => ({ ...c, driver_id: c.driver_id || c.id }));
  const resolvedName = driverName || cands.find((c) => c.driver_id === driverId)?.name || drivers.find((d) => (d.user_id || d.id) === driverId)?.name || '';

  const pick = (d, type) => {
    onChange(d.driver_id, type, d.name);
    setPicking(false);
  };

  return (
    <div className="min-w-[230px] text-xs" data-testid={`${testIdPrefix}-cell`}>
      <div className="flex items-center gap-1 text-slate-500">
        <Users className="w-3 h-3" />
        <span className="truncate" title="Original imported name" data-testid={`${testIdPrefix}-original`}>{originalName || <em className="text-slate-400">no driver on file</em>}</span>
      </div>
      {driverId && (
        <div className="mt-0.5 font-semibold text-slate-900 flex items-center gap-1" data-testid={`${testIdPrefix}-resolved`}>
          <span className="text-slate-400">→</span> {resolvedName || 'Registered driver'}
        </div>
      )}
      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
        <DriverMatchPill status={status} matchType={driverId ? matchType : null} testId={`${testIdPrefix}-pill`} />
        {driverId ? (
          <>
            <button type="button" data-testid={`${testIdPrefix}-change`} onClick={() => setPicking((v) => !v)} className="text-[11px] font-semibold text-[#002FA7] hover:underline">Change</button>
            <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange(null, null, null)} className="text-[11px] font-semibold text-slate-500 hover:underline">Unlink</button>
          </>
        ) : (
          <button type="button" data-testid={`${testIdPrefix}-assign`} onClick={() => setPicking((v) => !v)} className="text-[11px] font-semibold text-[#002FA7] hover:underline">
            {status === 'needs_confirmation' ? 'Other driver…' : 'Assign driver…'}
          </button>
        )}
      </div>

      {!driverId && status === 'needs_confirmation' && cands.length > 0 && (
        <div className="mt-1.5 space-y-1" data-testid={`${testIdPrefix}-candidates`}>
          <p className="text-[10px] text-amber-800">{cands.length} drivers match “{originalName}” — confirm one:</p>
          {cands.map((c) => (
            <button key={c.driver_id} type="button" data-testid={`${testIdPrefix}-confirm-${c.driver_id}`} onClick={() => pick(c, matchType || 'suggested')}
              className="w-full text-left px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-slate-800">
              <span className="font-semibold">{c.name}</span>{c.email && <span className="text-slate-500"> · {c.email}</span>}
            </button>
          ))}
        </div>
      )}

      {!driverId && status === 'unlinked' && (
        <p className="mt-1 text-[10px] text-slate-500">Will import as <b>Unlinked</b> — the name “{originalName || 'Unassigned (import)'}” is kept so dispatch can link it later. No driver account is created.</p>
      )}

      {picking && <DriverSearchList drivers={drivers} onPick={(d) => pick(d, 'manual')} testIdPrefix={`${testIdPrefix}-search`} autoFocus />}

      {driverId && sameNameCount > 0 && onApplyToAll && (
        <button type="button" data-testid={`${testIdPrefix}-apply-all`} onClick={() => onApplyToAll(driverId, matchType, resolvedName)}
          className="mt-1.5 text-[11px] font-semibold text-emerald-700 hover:underline">
          Apply to {sameNameCount} more row{sameNameCount === 1 ? '' : 's'} with “{originalName}” (this file only)
        </button>
      )}
    </div>
  );
};

export default DriverMatchCell;
